import { useMemo, useState } from 'react'
import { ExternalUserServiceIntegrationConfig, ExternalUserServiceIntegrationUpdate } from '@zocks-communications/client-sdk-js'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useGetUserActiveSessionsQuery } from '../../../../../store/media/media'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { type ZoomSettingsFormValues } from './models'

export const THREE_HOUR_IN_SECONDS = 3 * 60 * 60

export const useZoomSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)!

  const { data: userData, isLoading: isUserLoading } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [startedAfter] = useState(() => Math.floor(Date.now() / 1000) - THREE_HOUR_IN_SECONDS)
  const { data: activeSessions, isLoading: activeSessionLoading } = useGetUserActiveSessionsQuery(
    {
      accountId: accountId,
      userId: authenticatedUser.id,
      kind: 'external',
      startedAfter: startedAfter,
      pageRequest: { page: 0, size: 10 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [updateUserIntegration, { isLoading: isUpdating }] = useUpdateUserIntegrationMutation()

  const meetingInProgress = useMemo(() => {
    if (!activeSessionLoading && activeSessions) {
      return activeSessions.data.length > 0
    }
    return false
  }, [activeSessionLoading, activeSessions])

  const zoomIntegration = useMemo(() => {
    return userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
  }, [userData])

  const isAutoJoinEnforced = useMemo(() => {
    if (zoomIntegration?.configuration?.type === 'bot') {
      return zoomIntegration.configuration.enforcedAutoJoin !== null && zoomIntegration.configuration.enforcedAutoJoin !== undefined
    }
    return false
  }, [zoomIntegration])

  const save = async (data: ZoomSettingsFormValues) => {
    const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
    if (integration) {
      const { integrationType, removeSessionWhenNoRecording, autoJoin } = data
      const payload = {
        configuration: {
          type: integrationType,
          ...(integrationType === 'bot' && !isAutoJoinEnforced && { autoJoin }),
          ...(integrationType === 'recording' && { removeSessionWhenNoRecording }),
        } as ExternalUserServiceIntegrationConfig,
      } as ExternalUserServiceIntegrationUpdate

      await updateUserIntegration({
        accountId,
        integrationName: integration.serviceId,
        integrationId: integration.id,
        payload: payload,
      })
    }
  }

  return {
    zoomIntegration,
    isAutoJoinEnforced,
    meetingInProgress,
    isUserLoading,
    isUpdating,
    isFetching: isUserLoading || !userData,
    save,
  }
}
